import { ref, get, query, orderByKey, startAt, endAt } from 'firebase/database';
import { db } from '../firebase';
import { SLOT_STATUS_CLOSED, SLOT_CLOSE_REASONS_PATH, SLOT_CLOSE_REASON_MAX_LENGTH } from '../constants/bookingConst';
import { resolveAdminActor } from './auditLogUtil';

/**
 * Trims a typed close reason and caps it at SLOT_CLOSE_REASON_MAX_LENGTH.
 *
 * @param {string} text - raw textarea value
 * @returns {string} the reason to store ('' when nothing usable was typed)
 */
export const normalizeCloseReason = (text) => String(text || '').trim().slice(0, SLOT_CLOSE_REASON_MAX_LENGTH);

/**
 * The record written to slotCloseReasons/{date}/{timeSlot}/{court} when an
 * hour is closed.
 *
 * @param {string} reason - already-normalized reason
 * @returns {{reason: string, closedBy: string, closedAt: number}}
 */
export const buildCloseReasonRecord = (reason) => ({
  reason,
  closedBy: resolveAdminActor(),
  closedAt: Date.now(),
});

/**
 * Reads one cell's close reason out of a date's slotCloseReasons map.
 *
 * @param {object} reasonsMap - { [timeSlot]: { [court]: { reason } } }
 * @param {string} timeSlot
 * @param {string|number} court
 * @returns {string} the reason, or '' when none was recorded (e.g. an hour
 *   closed before reasons existed)
 */
export const getCloseReason = (reasonsMap, timeSlot, court) => (
  reasonsMap?.[timeSlot]?.[court]?.reason || ''
);

/**
 * Every CLOSED hour between two dates (inclusive), with its reason when one
 * was recorded. Driven off slotOverrides, not slotCloseReasons, so hours
 * closed before reasons existed still appear (with a blank reason).
 *
 * Both nodes are keyed by 'yyyy-MM-dd', so an orderByKey range bounds the
 * read on the server instead of downloading every date.
 *
 * @param {string} from - 'yyyy-MM-dd'
 * @param {string} to - 'yyyy-MM-dd'
 * @returns {Promise<Array<{date: string, timeSlot: string, court: string, reason: string, closedBy: string, closedAt: number|null}>>}
 * @throws {Error} when the database is not configured or the read fails
 */
export const fetchClosedSlotsInRange = async (from, to) => {
  if (!db) throw new Error('Database not configured — cannot load closed time slots.');
  const rangeOf = (path) => get(query(ref(db, path), orderByKey(), startAt(from), endAt(to)));
  // The reasons read is allowed to fail on its own. Until the new
  // slotCloseReasons rules are deployed to a client's project that read is
  // refused (permission_denied), and letting it reject took the closed hours
  // — and the whole export fetch with them — down too. The hours come from
  // slotOverrides; a missing reason just leaves that column blank.
  const [overridesSnap, reasonsSnap] = await Promise.all([
    rangeOf('slotOverrides'),
    rangeOf(SLOT_CLOSE_REASONS_PATH).catch((error) => {
      console.error('Close reasons could not be read — exporting closed slots without them:', error);
      return null;
    }),
  ]);
  const overrides = overridesSnap.exists() ? overridesSnap.val() : {};
  const reasons = reasonsSnap?.exists() ? reasonsSnap.val() : {};

  const rows = [];
  Object.entries(overrides).forEach(([date, slots]) => {
    Object.entries(slots || {}).forEach(([timeSlot, courts]) => {
      Object.entries(courts || {}).forEach(([court, status]) => {
        if (status !== SLOT_STATUS_CLOSED) return;
        const record = reasons?.[date]?.[timeSlot]?.[court] || {};
        rows.push({
          date,
          timeSlot,
          court,
          reason: record.reason || '',
          closedBy: record.closedBy || '',
          closedAt: record.closedAt || null,
        });
      });
    });
  });
  return rows;
};
